import { apiClient, matterApi } from '@/config/api';

export interface ConnectPerson {
  id: string;
  display_name: string;
  email?: string;
}

export interface ConnectThread {
  id: string;
  type: 'direct' | 'group' | 'matter' | 'object' | 'system';
  status?: 'active' | 'archived';
  title?: string;
  /** Legal-object binding for object threads (e.g. a DMS document/version conversation). */
  context?: {
    type: string;
    id: string;
    version_id?: string;
  };
  last_message?: {
    id: string;
    body: string;
    created_at: string;
    sender?: ConnectPerson;
  };
  unread_count: number;
  /** Unread messages in this thread that name the viewer (rail slice R2). */
  mention_count?: number;
  participants: ConnectPerson[];
  updated_at: string;
  /** The viewer's own star (rail slice R1): server-backed, follows them to every device. */
  starred_at?: string | null;
  /** The viewer's own mute on this thread. */
  is_muted?: boolean;
  /** Tier 3: the viewer's push level here, and the conversation's topic. */
  notify_level?: 'all' | 'mentions' | 'none';
  topic?: string | null;
}

export interface ConnectThreadPin {
  message_id: string;
  sequence: number;
  snippet: string;
  sender?: ConnectPerson;
  pinned_by?: ConnectPerson;
  pinned_at: string;
}

export interface ConnectThreadPage {
  threads: ConnectThread[];
  next_cursor: string | null;
}

export interface ConnectMessage {
  id: string;
  thread_id: string;
  body: string;
  kind: string;
  sequence: number;
  parent_message_id?: string;
  created_at: string;
  sender?: ConnectPerson;
  /** Surface the sender posted through ("Claude", "ChatGPT", "Lex"); absent when typed in EdgeLex. */
  posted_via?: string;
  mentions: ConnectPerson[];
  /** Anchored passage from the bound document version, when stored. */
  excerpt?: string;
  /** A threaded reply also sent to the main conversation stream. */
  broadcast?: boolean;
  status?: 'active' | 'redacted' | 'deleted';
  edited?: boolean;
  reactions?: Array<{ emoji: string; count: number; mine: boolean }>;
  /** Tier 3: pinned to the conversation. */
  pinned?: boolean;  /** Logged as a firm Decision (Beyond-Slack Wave 2). */
  decision?: { decided_by: string; decided_at: string };  /** Structured Request awaiting its assignee (Beyond-Slack Wave 4). */
  request?: { assignee_id: string; assignee: string; requested_by: string; due_date: string | null; status: 'pending' | 'done' | 'declined'; decided_at: string | null };
}

export interface ConnectUnreadCounts {
  unread_count: number;
  unread_threads: number;
  mention_count: number;
  direct_unread_count: number;
}

export const REACTION_EMOJIS = ['👍', '✅', '👀', '⚖️', '📌', '❗', '😂', '🙏'];

const unwrap = <T>(value: T | { data?: T }): T => {
  const wrapped = value as { data?: T };
  return typeof wrapped.data !== 'undefined' ? wrapped.data : (value as T);
};

export interface ConnectUnfurlCard {
  kind: 'document' | 'matter' | 'task';
  id: string;
  title: string;
  subtitle: string | null;
  badges: string[];
  url: string;
}

/**
 * Composer upload (Tier 3, 2026-09-09): a file dropped into a conversation becomes a
 * real DMS document — in the matter when the conversation is a matter channel — and the
 * message carries a link to it. Files never live in chat; the record lives in DMS.
 * Same two-step flow as the DMS upload dialog: stage the bytes, then create the document.
 */
export const uploadFileAsDocument = async (
  file: File,
  options: { token: string; matterId?: string | null; fetchImpl?: typeof fetch },
): Promise<{ id: string; title: string }> => {
  const fetchImpl = options.fetchImpl ?? fetch;
  const form = new FormData();
  form.append('file', file);
  const staged = await fetchImpl('/api/v1/dms/uploads', { method: 'POST', headers: { Authorization: `Bearer ${options.token}` }, body: form });
  if (!staged.ok) throw new Error(`Upload failed: ${staged.statusText}. ${(await staged.text()).slice(0, 200)}`);
  const uploadedFile = await staged.json();
  const created = await fetchImpl('/api/v1/dms/documents', {
    method: 'POST',
    headers: { Authorization: `Bearer ${options.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: file.name,
      description: 'Shared in an EdgeLex Connect conversation',
      matter_id: options.matterId || null,
      uploadedFile,
      ai_processing: true,
    }),
  });
  if (!created.ok) throw new Error(`Document creation failed: ${created.statusText}. ${(await created.text()).slice(0, 200)}`);
  const doc = await created.json() as { document_id?: string; id?: string; title?: string; data?: { document_id?: string; title?: string } };
  const id = doc.document_id ?? doc.id ?? doc.data?.document_id;
  if (!id) throw new Error('The document was created but its id did not come back.');
  return { id, title: doc.title ?? doc.data?.title ?? file.name };
};

export const connectApi = {
  /** Access-checked internal-object card for link unfurls; null = not
   * visible to this viewer (render nothing — never a broken card). */
  unfurlObject: async (kind: string, id: string): Promise<ConnectUnfurlCard | null> => {
    try {
      const response = await apiClient.get<{ card: ConnectUnfurlCard }>('/api/v1/connect/unfurl', { kind, id });
      return unwrap(response).card ?? null;
    } catch {
      return null;
    }
  },

  listThreads: async (view: 'active' | 'archived' = 'active'): Promise<ConnectThread[]> => {
    const response = await apiClient.get<{ threads: ConnectThread[] }>('/api/v1/connect/threads', view === 'archived' ? { view } : undefined);
    return unwrap(response).threads;
  },
  /** One page of the inbox (R4): starred first, then activity; `next_cursor` continues it. */
  listThreadsPage: async (options: { view?: 'active' | 'archived'; limit?: number; cursor?: string | null; unreadOnly?: boolean } = {}): Promise<ConnectThreadPage> => {
    const params: Record<string, string> = {};
    if (options.view === 'archived') params.view = 'archived';
    if (options.limit) params.limit = String(options.limit);
    if (options.cursor) params.cursor = options.cursor;
    if (options.unreadOnly) params.unread = '1';
    const response = await apiClient.get<ConnectThreadPage>('/api/v1/connect/threads', params);
    const page = unwrap(response);
    return { threads: page.threads || [], next_cursor: page.next_cursor ?? null };
  },

  /** Close (hide) a direct/group conversation — non-destructive; any new message reopens it. */
  closeThread: async (threadId: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/close`, {});
  },

  /** Archive/unarchive a group or matter channel — read-only, fully preserved. */
  setArchived: async (threadId: string, archived: boolean): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/archive`, { archived });
  },

  listDirectory: async (query: string): Promise<ConnectPerson[]> => {
    const response = await apiClient.get<{ people: ConnectPerson[] }>('/api/v1/connect/directory', { query });
    return unwrap(response).people;
  },

  createGroupThread: async (memberIds: string[], title?: string): Promise<ConnectThread> => {
    const response = await apiClient.post<{ thread: ConnectThread }>('/api/v1/connect/threads', {
      type: 'group',
      member_user_ids: memberIds,
      ...(title?.trim() ? { title: title.trim() } : {}),
    });
    return unwrap(response).thread;
  },

  ensureMatterChannel: async (matterId: string): Promise<ConnectThread> => {
    const response = await apiClient.post<{ thread: ConnectThread }>(`/api/v1/connect/matters/${matterId}/channel`);
    return unwrap(response).thread;
  },

  createDirectThread: async (personId: string): Promise<ConnectThread> => {
    const response = await apiClient.post<{ thread: ConnectThread }>('/api/v1/connect/threads', {
      type: 'direct',
      member_user_ids: [personId],
    });
    return unwrap(response).thread;
  },

  listMessages: async (threadId: string, options?: { before?: number; around?: number }): Promise<ConnectMessage[]> => {
    const query = options?.around !== undefined ? `?around=${options.around}` : options?.before !== undefined ? `?before=${options.before}` : '';
    const response = await apiClient.get<{ messages: ConnectMessage[] }>(`/api/v1/connect/threads/${threadId}/messages${query}`);
    return unwrap(response).messages;
  },

  sendMessage: async (
    threadId: string,
    body: string,
    mentionUserIds?: string[],
    options?: { parentMessageId?: string; alsoSendToConversation?: boolean; mentionScope?: 'channel' | 'here' | null },
  ): Promise<ConnectMessage> => {
    const clientMessageId = typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : `connect-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const response = await apiClient.post<{ message: ConnectMessage }>(`/api/v1/connect/threads/${threadId}/messages`, {
      body,
      client_message_id: clientMessageId,
      ...(mentionUserIds && mentionUserIds.length ? { mention_user_ids: mentionUserIds } : {}),
      ...(options?.parentMessageId ? { parent_message_id: options.parentMessageId } : {}),
      ...(options?.mentionScope ? { mention_scope: options.mentionScope } : {}),
      ...(options?.alsoSendToConversation ? { also_send_to_conversation: true } : {}),
    });
    return unwrap(response).message;
  },

  markDecision: async (threadId: string, messageId: string): Promise<ConnectMessage> => {
    const response = await apiClient.post<{ message: ConnectMessage }>(`/api/v1/connect/threads/${threadId}/messages/${messageId}/decision`, {});
    return unwrap(response).message;
  },

  unmarkDecision: async (threadId: string, messageId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/connect/threads/${threadId}/messages/${messageId}/decision`);
  },

  listEthicalWalls: async (matterId: string): Promise<Array<{ id: string; screened_user_id: string; screened_user_name: string | null; reason: string; created_by_name: string | null; created_at: string; released_at: string | null }>> => {
    const response = await apiClient.get<{ walls: Array<{ id: string; screened_user_id: string; screened_user_name: string | null; reason: string; created_by_name: string | null; created_at: string; released_at: string | null }> }>(`/api/v1/connect/matters/${matterId}/ethical-walls`);
    return unwrap(response).walls;
  },

  createEthicalWall: async (matterId: string, screenedUserId: string, reason: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/matters/${matterId}/ethical-walls`, { screened_user_id: screenedUserId, reason });
  },

  releaseEthicalWall: async (matterId: string, wallId: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/matters/${matterId}/ethical-walls/${wallId}/release`, {});
  },

  fileThreadToMatter: async (threadId: string): Promise<{ document_id: string; title: string; message_count: number }> => {
    const response = await apiClient.post<{ document_id: string; title: string; message_count: number }>(
      `/api/v1/connect/threads/${threadId}/file-to-matter`,
      {},
    );
    return unwrap(response);
  },

  /** Light DMS document search for the composer's attach picker. */
  searchDocumentsForAttach: async (query: string): Promise<Array<{ id: string; title: string }>> => {
    try {
      const response = await apiClient.get<Record<string, unknown>>('/api/v1/dms/documents/search', { q: query, limit: 6 });
      const data = unwrap(response) as { documents?: Array<{ document_id?: string; title?: string }>; results?: Array<{ document_id?: string; title?: string }> };
      const rows = data.documents ?? data.results ?? [];
      return rows
        .filter((row) => typeof row.document_id === 'string' && typeof row.title === 'string')
        .map((row) => ({ id: row.document_id as string, title: row.title as string }));
    } catch {
      return [];
    }
  },

  /** Fire-and-forget typing signal; never awaited by callers on purpose. */
  sendTyping: (threadId: string): void => {
    void apiClient.post(`/api/v1/connect/threads/${threadId}/typing`, {}).catch(() => undefined);
  },

  createRequest: async (threadId: string, input: { assigneeUserId: string; text: string; dueDate?: string | null }): Promise<ConnectMessage> => {
    const response = await apiClient.post<{ message: ConnectMessage }>(`/api/v1/connect/threads/${threadId}/requests`, {
      assignee_user_id: input.assigneeUserId,
      text: input.text,
      due_date: input.dueDate ?? undefined,
    });
    return unwrap(response).message;
  },

  decideRequest: async (threadId: string, messageId: string, decision: 'done' | 'declined'): Promise<ConnectMessage> => {
    const response = await apiClient.post<{ message: ConnectMessage }>(`/api/v1/connect/threads/${threadId}/requests/${messageId}/decide`, { decision });
    return unwrap(response).message;
  },

  billMessage: async (
    threadId: string,
    messageId: string,
    input: { durationHours: number; description: string },
  ): Promise<{ time_entry_id: string; duration_hours: number }> => {
    const response = await apiClient.post<{ time_entry_id: string; duration_hours: number }>(
      `/api/v1/connect/threads/${threadId}/messages/${messageId}/bill`,
      { duration_hours: input.durationHours, description: input.description },
    );
    return unwrap(response);
  },

  toggleReaction: async (threadId: string, messageId: string, emoji: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/messages/${messageId}/reactions`, { emoji });
  },

  editMessage: async (threadId: string, messageId: string, body: string): Promise<ConnectMessage> => {
    const response = await apiClient.patch<{ message: ConnectMessage }>(`/api/v1/connect/threads/${threadId}/messages/${messageId}`, { body });
    return unwrap(response).message;
  },

  deleteMessage: async (threadId: string, messageId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/connect/threads/${threadId}/messages/${messageId}`);
  },

  setMuted: async (threadId: string, muted: boolean): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/mute`, { muted });
  },
  listPins: async (threadId: string): Promise<ConnectThreadPin[]> => {
    const response = await apiClient.get<{ pins: ConnectThreadPin[] }>(`/api/v1/connect/threads/${threadId}/pins`);
    return unwrap(response).pins || [];
  },
  setPinned: async (threadId: string, messageId: string, pinned: boolean): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/messages/${messageId}/pin`, { pinned });
  },
  setTopic: async (threadId: string, topic: string): Promise<string | null> => {
    const response = await apiClient.put<{ topic: string | null }>(`/api/v1/connect/threads/${threadId}/topic`, { topic });
    return unwrap(response).topic ?? null;
  },
  setNotifyLevel: async (threadId: string, level: 'all' | 'mentions' | 'none'): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/notify-level`, { level });
  },
  /** Where each other member has read up to (Tier 3 seen-by). */
  getReadPositions: async (threadId: string): Promise<Array<{ user_id: string; last_read_sequence: number | null; last_read_at: string | null }>> => {
    const response = await apiClient.get<{ positions: Array<{ user_id: string; last_read_sequence: number | null; last_read_at: string | null }> }>(`/api/v1/connect/threads/${threadId}/read-positions`);
    return unwrap(response).positions || [];
  },
  /** One cheap aggregate for the nav badge (shared with mobile); mute-aware, mentions counted everywhere. */
  getUnreadCount: async (): Promise<ConnectUnreadCounts> => {
    const response = await apiClient.get<ConnectUnreadCounts>('/api/v1/connect/unread-count');
    return unwrap(response);
  },
  setStarred: async (threadId: string, starred: boolean): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/star`, { starred });
  },

  markUnread: async (threadId: string, messageId: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/unread`, { message_id: messageId });
  },

  markRead: async (threadId: string, messageId?: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/threads/${threadId}/read`, messageId ? { message_id: messageId } : {});
  },
};

export interface ConnectThreadActivityItem {
  id: string;
  event_type: string;
  label: string;
  created_at: string;
}

export interface MatterDeadlineEvent {
  id: string;
  title: string;
  start_time: string;
  event_type?: string;
}

export interface ConnectSearchHit {
  message_id: string;
  /** Position in the thread, so the hit can be opened in place (R3). */
  sequence: number;
  thread_id: string;
  thread_type: ConnectThread['type'];
  thread_title: string | null;
  snippet: string;
  created_at: string;
  sender?: ConnectPerson;
}

/** Server-side search scope; ISO dates. Resolved from `in:` / `from:` / `before:` / `after:` on the client. */
export interface ConnectSearchFilters {
  threadId?: string;
  senderId?: string;
  before?: string;
  after?: string;
}

/** One card in the Threads inbox (V1). */
export interface ConnectFollowedThread {
  root_message_id: string;
  root_sequence: number;
  thread_id: string;
  thread_type: ConnectThread['type'];
  thread_title: string | null;
  context_type: string | null;
  root_snippet: string;
  root_sender?: ConnectPerson;
  root_created_at: string;
  reply_count: number;
  last_reply_at: string;
  last_reply_sender?: ConnectPerson;
  unread_replies: number;
  started_by_me: boolean;
}

/** One row of the Activity feed (V2): something that happened to or for the viewer. */
export interface ConnectActivityItem {
  kind: 'mention' | 'reaction' | 'request_assigned' | 'request_decided' | 'decision';
  at: string;
  actor?: ConnectPerson;
  thread_id: string;
  thread_type: ConnectThread['type'];
  thread_title: string | null;
  message_id: string;
  sequence: number;
  snippet: string;
  /** Mentions only: the viewer has not read the message yet. */
  unread: boolean;
  /** Reactions: the emoji; requests: the status; free-form otherwise. */
  detail?: string | null;
}

/** A saved composer draft or a scheduled send (V3). */
export interface ConnectDraft {
  id: string;
  thread_id: string;
  thread_type: ConnectThread['type'];
  thread_title: string | null;
  body: string;
  mention_user_ids: string[];
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  send_at: string | null;
  last_error: string | null;
  updated_at: string;
}

export interface ConnectSavedItem {
  message_id: string;
  thread_id: string;
  thread_type: ConnectThread['type'];
  thread_title: string | null;
  snippet: string;
  message_created_at: string;
  saved_at: string;
  remind_at: string | null;
  sender?: ConnectPerson;
}

export const connectExtrasApi = {
  searchMessages: async (query: string, filters: ConnectSearchFilters = {}): Promise<ConnectSearchHit[]> => {
    const params: Record<string, string> = { q: query };
    if (filters.threadId) params.thread_id = filters.threadId;
    if (filters.senderId) params.sender_id = filters.senderId;
    if (filters.before) params.before = filters.before;
    if (filters.after) params.after = filters.after;
    const response = await apiClient.get<{ hits: ConnectSearchHit[] }>('/api/v1/connect/search', params);
    return unwrap(response).hits || [];
  },

  /** Every reply thread the viewer wrote or replied in, newest reply first (V1). */
  listFollowedThreads: async (limit = 50): Promise<ConnectFollowedThread[]> => {
    const response = await apiClient.get<{ threads: ConnectFollowedThread[] }>('/api/v1/connect/followed-threads', { limit: String(limit) });
    return unwrap(response).threads || [];
  },
  /** Mentions of the viewer, reactions to their messages, requests and decisions for them (V2). */
  listActivity: async (limit = 100): Promise<ConnectActivityItem[]> => {
    const response = await apiClient.get<{ items: ConnectActivityItem[] }>('/api/v1/connect/activity', { limit: String(limit) });
    return unwrap(response).items || [];
  },
  /** Drafts and scheduled sends the viewer owns (V3). */
  listDrafts: async (): Promise<ConnectDraft[]> => {
    const response = await apiClient.get<{ drafts: ConnectDraft[] }>('/api/v1/connect/drafts');
    return unwrap(response).drafts || [];
  },
  /** Upsert the viewer's draft for a conversation; an empty body removes it. */
  saveDraft: async (threadId: string, body: string, mentionUserIds: string[] = []): Promise<void> => {
    await apiClient.put(`/api/v1/connect/threads/${threadId}/draft`, { body, mention_user_ids: mentionUserIds });
  },
  scheduleSend: async (threadId: string, body: string, sendAt: string, mentionUserIds: string[] = []): Promise<ConnectDraft> => {
    const response = await apiClient.post<{ draft: ConnectDraft }>(`/api/v1/connect/threads/${threadId}/schedule`, { body, send_at: sendAt, mention_user_ids: mentionUserIds });
    return unwrap(response).draft;
  },
  sendDraftNow: async (draftId: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/drafts/${draftId}/send-now`, {});
  },
  cancelDraft: async (draftId: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/drafts/${draftId}/cancel`, {});
  },
  listSaved: async (): Promise<ConnectSavedItem[]> => {
    const response = await apiClient.get<{ saved: ConnectSavedItem[] }>('/api/v1/connect/saved');
    return unwrap(response).saved || [];
  },

  saveMessage: async (messageId: string, remindAt?: string): Promise<void> => {
    await apiClient.post(`/api/v1/connect/saved/${messageId}`, remindAt ? { remind_at: remindAt } : {});
  },

  unsaveMessage: async (messageId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/connect/saved/${messageId}`);
  },

  /** Who has EdgeLex open right now (dms user ids) plus the caller's own id. */
  getPresence: async (): Promise<{ self: string | null; online: string[] }> => {
    const response = await apiClient.get<{ self: string | null; online: string[] }>('/api/v1/connect/presence');
    const data = unwrap(response);
    return { self: data.self ?? null, online: Array.isArray(data.online) ? data.online : [] };
  },

  getThreadActivity: async (threadId: string): Promise<ConnectThreadActivityItem[]> => {
    const response = await apiClient.get<{ activity: ConnectThreadActivityItem[] }>(`/api/v1/connect/threads/${threadId}/activity`);
    return unwrap(response).activity || [];
  },

  getMatterUpcomingEvents: async (matterId: string): Promise<MatterDeadlineEvent[]> => {
    const response = await apiClient.get<{ events: MatterDeadlineEvent[] }>(`/api/v1/calendars/matters/${matterId}/events`, { limit: 25 });
    const events = unwrap(response).events || [];
    const now = Date.now();
    return events
      .filter((event) => new Date(event.start_time).getTime() >= now)
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      .slice(0, 3);
  },
};

export interface SaveableMatter {
  matter_id: string;
  description: string;
  matter_number: string;
  client_name?: string;
}

/** Active matters the caller can see, via the same matters API the shared pickers use. */
export const matterNotesApi = {
  listActiveMatters: async (): Promise<SaveableMatter[]> => {
    const response: any = await matterApi.getAll();
    const raw: any[] = Array.isArray(response) ? response : (response.data || response.matters || []);
    const mapped: SaveableMatter[] = raw.map((m: any) => ({
      matter_id: String(m.matter_id || m.id || ''),
      description: String(m.title || m.matter_title || m.name || m.description || ''),
      matter_number: String(m.matter_number || m.matterNumber || ''),
      client_name: String(m.client?.client_name || m.client_name || m.clientName || '') || undefined,
    })).filter((m: SaveableMatter) => m.matter_id);
    const active = mapped.filter((_, index) => {
      const source = raw[index];
      const stage = String(source?.stage || source?.status || '').toLowerCase();
      return stage === 'active' || stage === '';
    });
    // Defensive: if the active filter empties an otherwise non-empty list,
    // show everything rather than a useless empty picker.
    return active.length > 0 ? active : mapped;
  },

  saveMessageToMatter: async (matterId: string, title: string, contentText: string): Promise<void> => {
    const lines = contentText.replace(/\r\n/g, '\n').split('\n');
    const contentJson = {
      root: {
        type: 'root', format: '', indent: 0, version: 1, direction: 'ltr',
        children: lines.map((line) => ({
          type: 'paragraph', format: '', indent: 0, version: 1, direction: 'ltr',
          children: [{ type: 'text', detail: 0, format: 0, mode: 'normal', style: '', text: line, version: 1 }],
        })),
      },
    };
    await apiClient.post('/api/v1/notes', {
      title,
      content_text: contentText,
      content_json: contentJson,
      matter_id: matterId,
      note_type: 'general',
      priority: 'medium',
      confidentiality: 'public',
      tags: ['connect'],
    });
  },
};

/** Creates an instant EdgeMeet room ("huddle") for a conversation. */
export const meetNowApi = {
  /** Truth-read: current status per meeting id; the server heals stale actives on the way. */
  fetchStatuses: async (meetingIds: string[]): Promise<Array<{ id: string; status: string }>> => {
    if (!meetingIds.length) return [];
    const response = await apiClient.get<{ statuses: Array<{ id: string; status: string }> }>('/api/v1/meeting/rooms/statuses', { ids: meetingIds.join(',') });
    return unwrap(response).statuses || [];
  },

  createRoom: async (title: string, options?: { matterId?: string; connectThreadId?: string }): Promise<{ meetingId: string; title: string }> => {
    const response = await apiClient.post<Record<string, any>>('/api/v1/meeting/rooms', {
      title,
      roomType: 'meeting',
      recordingEnabled: true,
      ...(options?.matterId ? { matterId: options.matterId } : {}),
      ...(options?.connectThreadId ? { connectThreadId: options.connectThreadId } : {}),
    });
    const payload: any = unwrap(response);
    const meetingId = String(payload.meetingId || payload.meeting_id || payload.data?.meetingId || '');
    if (!meetingId) throw new Error('The meeting room could not be created.');
    return { meetingId, title: String(payload.title || title) };
  },
};

export const MEETING_CARD_HEADER = '🎥 Meeting started';

export const formatMeetingCard = (title: string, meetingId: string): string =>
  [MEETING_CARD_HEADER, title, `Join: ${window.location.origin}/meet/${meetingId}/room`].join('\n');

export const parseMeetingCard = (message: ConnectMessage): { title: string; meetingId: string } | null => {
  if (!message.body.startsWith(MEETING_CARD_HEADER)) return null;
  const lines = message.body.split('\n');
  const joinLine = lines.find((line) => line.startsWith('Join: '));
  const match = joinLine?.match(/\/meet\/([0-9a-f-]{36})\/room/i);
  if (!match) return null;
  return { title: (lines[1] || 'Meeting').trim(), meetingId: match[1] };
};

export const MEETING_END_CARD_HEADER = '📄 Meeting ended';

export const parseMeetingEndCard = (message: ConnectMessage): { title: string; meetingId: string } | null => {
  if (!message.body.startsWith(MEETING_END_CARD_HEADER)) return null;
  const match = message.body.match(/\/meet\/([0-9a-f-]{36})/i);
  if (!match) return null;
  return { title: (message.body.split('\n')[1] || 'Meeting').trim(), meetingId: match[1] };
};
